/**
 * Domänlogik för Uppföljning estimat
 * Innehåller state-beräkningar, parsing och formatering
 */

/**
 * Beräknar vilket scenario/state uppföljningen befinner sig i
 * @param {Object} params
 * @param {Object} params.byggkonsultProposals - Byggkonsultens skickade tidsförslag
 * @param {Object} params.estimatFollowup - Bokat uppföljningsmöte från backend
 * @param {string} params.bookedFollowupSlot - Lokalt bokat slot (från state)
 * @param {Object} params.customerComments - Kundens kommentarer (inkl. __meetingRequest__)
 * @returns {Object} { scenario, data }
 */
export const getFollowupState = ({
  byggkonsultProposals,
  estimatFollowup,
  bookedFollowupSlot,
  customerComments,
}) => {
  // SCENARIO A: Byggkonsult har skickat tidsförslag (prioriteras högst)
  // A1: Kund accepterade - kolla detta FÖRST även om proposals rensats
  if (byggkonsultProposals?.customerResponse?.status === 'accepted') {
    return {
      scenario: 'KONSULT_PROPOSAL_ACCEPTED',
      data: {
        acceptedProposal: byggkonsultProposals.customerResponse.acceptedProposal,
        acceptedAt: byggkonsultProposals.customerResponse.acceptedAt,
        proposals: byggkonsultProposals.proposals || [],
        message: byggkonsultProposals.message,
      },
    };
  }

  // A2: Väntar på svar eller kund har svarat med meddelande (kräver proposals)
  if (byggkonsultProposals?.proposals?.length > 0) {
    return {
      scenario: 'KONSULT_PROPOSAL_WAITING',
      data: {
        proposals: byggkonsultProposals.proposals,
        message: byggkonsultProposals.message,
        customerResponse: byggkonsultProposals.customerResponse,
        sentAt: byggkonsultProposals.sentAt,
        emailSent: byggkonsultProposals.emailSent,
        beställare2EmailSent: byggkonsultProposals.beställare2EmailSent,
      },
    };
  }

  // SCENARIO B: Uppföljning redan bokad
  const bookedSlot = getBookedSlot({ estimatFollowup, bookedFollowupSlot });
  if (bookedSlot) {
    return {
      scenario: 'MEETING_BOOKED',
      data: {
        bookedSlot,
        teamsUrl: estimatFollowup?.teamsJoinUrl,
      },
    };
  }

  // SCENARIO C: Kund har skickat tidsförslag FÖRST (nytt: från estimatFollowup.customerResponse.proposals)
  if (estimatFollowup?.customerResponse?.proposals && estimatFollowup.customerResponse.proposals.length > 0) {
    const note = estimatFollowup.customerResponse.proposalNote || '';
    const slots = estimatFollowup.customerResponse.proposals;
    
    return {
      scenario: 'CUSTOMER_REQUEST_WITH_TIMES',
      data: { note, slots },
    };
  }

  // SCENARIO D: Kund har begärt uppföljning via __meetingRequest__ (legacy, bakåtkompatibilitet)
  const meetingRequest = customerComments?.__meetingRequest__;
  if (meetingRequest) {
    const note = extractMeetingNote(meetingRequest);
    const slots = extractMeetingSlots(meetingRequest);

    // D1: Med kommentar och tider
    if (note && slots.length > 0) {
      return {
        scenario: 'CUSTOMER_REQUEST_WITH_TIMES',
        data: { note, slots },
      };
    }

    // D2: Med kommentar, inga tider
    if (note) {
      return {
        scenario: 'CUSTOMER_REQUEST_NO_TIMES',
        data: { note },
      };
    }

    // D3: Inga tider valda
    if (slots.length === 0) {
      return {
        scenario: 'CUSTOMER_REQUEST_NO_TIMES',
        data: { note: 'Kunden begärde uppföljning men valde inga tider.' },
      };
    }

    // D4: Bara tider, ingen kommentar
    return {
      scenario: 'CUSTOMER_REQUEST_WITH_TIMES',
      data: { note: '', slots },
    };
  }

  // SCENARIO E: Ingen meeting request alls
  return {
    scenario: 'NO_REQUEST',
    data: {},
  };
};

/**
 * Hämtar det bokade tidsluckan (från state eller estimatFollowup)
 */
export const getBookedSlot = ({ estimatFollowup, bookedFollowupSlot }) => {
  if (bookedFollowupSlot) {
    return bookedFollowupSlot;
  }

  if (!estimatFollowup) {
    return null;
  }

  // Formatera från meetingAt (ignorera selectedSlot som kan vara felformaterat)
  if (estimatFollowup.meetingAt) {
    return formatBookedSlotFromDate(estimatFollowup.meetingAt);
  }

  // Fallback till displaySlot eller selectedSlot
  return estimatFollowup.displaySlot || estimatFollowup.selectedSlot || null;
};

/**
 * Formaterar en ISO-datum till "veckdag dag/månad kl HH:MM"
 * Exempel: "mån 15/3 kl 14:00"
 */
export const formatBookedSlotFromDate = (isoDate) => {
  const date = new Date(isoDate);
  const weekdays = ['sön', 'mån', 'tis', 'ons', 'tors', 'fre', 'lör'];
  
  const weekday = weekdays[date.getUTCDay()];
  const day = date.getUTCDate();
  const month = date.getUTCMonth() + 1;
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');

  return `${weekday} ${day}/${month} kl ${hours}:${minutes}`;
};

/**
 * Extraherar kommentar/note från meetingRequest
 */
export const extractMeetingNote = (meetingRequest) => {
  if (typeof meetingRequest === 'object') {
    return meetingRequest.note || meetingRequest.text || '';
  }
  return meetingRequest || '';
};

/**
 * Extraherar tidsförslag från meetingRequest
 */
export const extractMeetingSlots = (meetingRequest) => {
  if (typeof meetingRequest === 'object' && Array.isArray(meetingRequest.slots)) {
    return meetingRequest.slots;
  }
  return [];
};

/**
 * Parsar ett displaySlot-format till { date, time }
 * Exempel: "mån 15/3 kl 14:00" → { date: "2025-03-15", time: "14:00" }
 */
export const parseTimeSlot = (displaySlot) => {
  const match = displaySlot.match(/(\d{1,2})\/(\d{1,2}).*kl\s*(\d{2}:\d{2})/i);
  
  if (!match) {
    throw new Error('Kunde inte tolka vald tid');
  }
  
  const [, day, month, time] = match;
  const year = new Date().getFullYear();
  const dateIso = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  
  return { date: dateIso, time };
};
